// App.js
import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import Header from './Components/Header';
import Main from './Components/Main.jsx';
import Team from './Components/Team.jsx';
import Speakers from './Components/Speakers.jsx';
import './App.css';

// Enhanced Scroll to Top Button Component
const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Show button when page is scrolled down and calculate scroll progress
  const handleScroll = () => {
    const scrollTop = window.pageYOffset;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = (scrollTop / docHeight) * 100;
    
    setScrollProgress(scrollPercent);
    setIsVisible(scrollTop > 300);
  };

  // Ultra Smooth Scroll Function - Replace your scrollToTop function with this
  const scrollToTop = () => {
    // Method 1: Enhanced native smooth scroll with custom duration
    const scrollToTopSmooth = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };

    // Method 2: Custom ultra smooth scroll with easing
    const scrollToTopCustom = () => {
      const currentScrollY = window.scrollY;
      const duration = 1000; // 1 second
      const startTime = performance.now();
      
      // Easing function for ultra smooth animation
      const easeInOutCubic = (t) => {
        return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
      };

      const animateScroll = (currentTime) => {
        const elapsedTime = currentTime - startTime;
        const progress = Math.min(elapsedTime / duration, 1);
        const easedProgress = easeInOutCubic(progress);
        
        window.scrollTo(0, currentScrollY * (1 - easedProgress));
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      
      requestAnimationFrame(animateScroll);
    };

    // Method 3: Ultra smooth with spring physics
    const scrollToTopSpring = () => {
      const startY = window.scrollY;
      const targetY = 0;
      let currentY = startY;
      let velocity = 0;
      
      const spring = 0.15; // Spring strength
      const friction = 0.8; // Friction/damping
      
      const animate = () => {
        const distance = targetY - currentY;
        const acceleration = distance * spring;
        
        velocity += acceleration;
        velocity *= friction;
        currentY += velocity;
        
        window.scrollTo(0, currentY);
        
        if (Math.abs(distance) > 0.1 || Math.abs(velocity) > 0.1) {
          requestAnimationFrame(animate);
        } else {
          window.scrollTo(0, targetY); // Ensure exact position
        }
      };
      
      animate();
    };

    // Choose which method to use (uncomment the one you prefer)
    
    // For standard smooth scroll (fastest)
    scrollToTopSmooth();
    
    // For custom eased scroll (smooth with custom timing)
    // scrollToTopCustom();
    
    // For spring physics scroll (most fluid)
    // scrollToTopSpring();
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="scroll-to-top">
      {isVisible && (
        <button
          onClick={scrollToTop}
          className="scroll-btn"
          aria-label="Scroll to top"
        >
          {/* Progress circle */}
          <svg className="progress-ring" width="56" height="56">
            <circle
              className="progress-ring-bg"
              cx="28"
              cy="28"
              r="26"
            />
            <circle
              className="progress-ring-circle"
              cx="28"
              cy="28"
              r="26"
              style={{
                strokeDashoffset: `${163 - (scrollProgress * 163) / 100}`
              }}
            />
          </svg>
          
          {/* Arrow icon */}
          <svg
            className="arrow-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7 14L12 9L17 14"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
           
      <style jsx>{`
        .scroll-to-top {
          position: fixed;
          bottom: 30px;
          right: 30px;
          z-index: 1000;
        }

        .scroll-btn {
          position: relative;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 50%;
          width: 56px;
          height: 56px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          animation: slideIn 0.5s ease-out;
          overflow: hidden;
        }

        .scroll-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .scroll-btn:hover::before {
          opacity: 1;
        }

        .scroll-btn:hover {
          transform: translateY(-4px) scale(1.1);
          box-shadow: 0 12px 40px rgba(102, 126, 234, 0.4);
        }

        .scroll-btn:active {
          transform: translateY(-2px) scale(1.05);
        }

        .progress-ring {
          position: absolute;
          top: 0;
          left: 0;
          transform: rotate(-90deg);
          pointer-events: none;
        }

        .progress-ring-bg {
          fill: none;
          stroke: rgba(255, 255, 255, 0.2);
          stroke-width: 2;
        }

        .progress-ring-circle {
          fill: none;
          stroke: #ffffff;
          stroke-width: 3;
          stroke-linecap: round;
          stroke-dasharray: 163;
          stroke-dashoffset: 163;
          transition: stroke-dashoffset 0.1s ease;
          filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.6));
        }

        .arrow-icon {
          position: relative;
          z-index: 2;
          animation: bounce 2s infinite;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-3px);
          }
          60% {
            transform: translateY(-2px);
          }
        }

        /* Pulse effect on hover */
        .scroll-btn::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }

        .scroll-btn:hover::after {
          width: 120%;
          height: 120%;
        }

        /* Responsive design */
        @media (max-width: 768px) {
          .scroll-to-top {
            bottom: 20px;
            right: 20px;
          }
          
          .scroll-btn {
            width: 50px;
            height: 50px;
          }

          .progress-ring {
            width: 50px;
            height: 50px;
          }

          .progress-ring-bg,
          .progress-ring-circle {
            cx: 25;
            cy: 25;
            r: 23;
          }

          .progress-ring-circle {
            stroke-dasharray: 144;
          }

          .arrow-icon {
            width: 18px;
            height: 18px;
          }
        }

        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
          .scroll-btn {
            background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
            box-shadow: 0 8px 32px rgba(26, 26, 46, 0.5);
          }

          .scroll-btn::before {
            background: linear-gradient(135deg, #0f3460 0%, #1a1a2e 100%);
          }

          .scroll-btn:hover {
            box-shadow: 0 12px 40px rgba(26, 26, 46, 0.6);
          }
        }

        /* Accessibility improvements */
        @media (prefers-reduced-motion: reduce) {
          .scroll-btn {
            animation: none;
          }

          .arrow-icon {
            animation: none;
          }

          .scroll-btn:hover {
            transform: none;
          }
        }

        /* Focus states for accessibility */
        .scroll-btn:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.5);
        }

        .scroll-btn:focus:not(:focus-visible) {
          box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
        }
      `}</style>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Header />
      
      <Routes>
        <Route path="/" element={<Main />} />
        <Route path="/team" element={<Team />} />
        <Route path="/speakers" element={<Speakers />} />
      </Routes>

      {/* <Footer /> */}
      
      <ScrollToTopButton />
    </div>
  );
}

export default App;